import { Menu } from 'antd';
import { useEffect, useState } from 'react';

import { useGetTaskTemplatesLazyQuery } from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { useFeatures } from 'utils/features';
import { TASK_STEP_IMPROVEMENTS } from 'utils/release-toggles';

import { formatTemplatesMenu } from './helpers';
import { StyledMenu, StyledSelect } from './styles';
import { type ITaskTemplatesChooserProps } from './types';

const { SubMenu } = Menu;

export const TaskTemplatesChooser: React.FunctionComponent<ITaskTemplatesChooserProps> = ({
  workContainerTypes,
  onClickHandler,
  displayType,
  taskStepsRequired = false,
  costManagementRequired = false,
  disabled = false,
  menuTitle = 'Templates',
}) => {
  const { TASK_TEMPLATES } = useFeatures().features;
  const { hasReleaseToggle } = useAccountContext();
  const hasStepImprovementsRT = hasReleaseToggle(TASK_STEP_IMPROVEMENTS);

  const [templatesOptions, setTemplatesOptions] = useState<JSX.Element[]>();

  const [
    getAllTaskTemplates,
    {
      data: { getTaskTemplates: templatesData = null } = {},
      loading: templatesLoading,
      error: templatesError,
    },
  ] = useGetTaskTemplatesLazyQuery();

  useEffect(() => {
    if (TASK_TEMPLATES) {
      getAllTaskTemplates({
        variables: {
          workContainerTypes,
          taskStepsRequired,
        },
      });
    }
  }, [TASK_TEMPLATES, getAllTaskTemplates, workContainerTypes, taskStepsRequired]);

  useEffect(() => {
    if (templatesData) {
      let filteredOptions = costManagementRequired
        ? templatesData.filter(
            template =>
              (template.costResourceActiveDefaults &&
                template.costResourceActiveDefaults.length > 0) ||
              (template.rateDefaults && template.rateDefaults.length > 0)
          )
        : templatesData;
      // Only display global templates if the user has the toggle
      if (!hasStepImprovementsRT) {
        filteredOptions = filteredOptions.filter(template => !template.global);
      }
      if (filteredOptions.length > 0) {
        setTemplatesOptions(
          formatTemplatesMenu(filteredOptions, displayType, hasStepImprovementsRT)
        );
      } else {
        setTemplatesOptions([
          <Menu.Item disabled={true} key="none">
            No Templates have been created
          </Menu.Item>,
        ]);
      }
    }
  }, [
    templatesData,
    templatesLoading,
    templatesError,
    displayType,
    costManagementRequired,
    hasStepImprovementsRT,
  ]);

  return displayType === 'menu' ? (
    <StyledMenu onClick={e => onClickHandler(e)}>
      <SubMenu key="templates-menu" title={menuTitle}>
        {templatesOptions?.length && templatesOptions?.length > 0 ? (
          <Menu.ItemGroup
            //@ts-ignore This style is valid and works, but TS doesn't like it.
            style={{
              overflowY: 'auto',
              maxHeight: '300px',
            }}
          >
            {templatesOptions}
          </Menu.ItemGroup>
        ) : (
          <Menu.Item disabled={true} key="none">
            No Templates have been created
          </Menu.Item>
        )}
      </SubMenu>
    </StyledMenu>
  ) : (
    <StyledSelect
      data-testid="template-chooser"
      onSelect={e => onClickHandler(e)}
      placeholder={'Select Template...'}
      disabled={disabled}
      allowClear={true}
      onClear={() => onClickHandler(undefined)}
    >
      {templatesOptions?.length && templatesOptions?.length && templatesOptions}
    </StyledSelect>
  );
};
